import { Info, Lock } from "lucide-react";
import { useId } from "react";
import { roleBlock, type Me, type Role } from "../lib/me";

/**
 * A button for an action that needs a role. For anyone below it the button stays visible,
 * disabled, with the reason beside it, so a viewer can see what exists and why it is not theirs.
 * The server refuses the call anyway; this only keeps a person from finding that out by trying.
 *
 * `block` is a reason that is not about the role, such as a case that is not ready for the move.
 * The role's reason wins when both apply.
 */
export function RoleButton({
  role, me, block = null, children, disabled, ...rest
}: { role: Role; me: Me | null | undefined; block?: string | null } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const roleReason = roleBlock(me, role);
  const reason = roleReason ?? block;
  const id = useId();
  if (!reason) return <button disabled={disabled} {...rest}>{children}</button>;
  return (
    <span className="role-gated">
      <button {...rest} disabled aria-describedby={id} title={reason} onClick={undefined}>{children}</button>
      <span id={id} className="role-reason">
        {roleReason ? <Lock size={12} aria-hidden="true" /> : <Info size={12} aria-hidden="true" />} {reason}
      </span>
    </span>
  );
}

/**
 * A group of controls for a role: inputs a viewer may not type into, or several buttons that share
 * one reason. Below the role the whole group is a disabled fieldset with the reason once, so the
 * fields stay readable and nothing in them can be changed or sent.
 */
export function RoleGroup({
  role, me, children, className, label,
}: { role: Role; me: Me | null | undefined; children: React.ReactNode; className?: string; label?: string }) {
  const reason = roleBlock(me, role);
  const id = useId();
  return (
    <fieldset className={`role-group${className ? ` ${className}` : ""}`} disabled={!!reason}
              aria-describedby={reason ? id : undefined} aria-label={label}>
      {children}
      {reason && <span id={id} className="role-reason"><Lock size={12} aria-hidden="true" /> {reason}</span>}
    </fieldset>
  );
}

/** A section or a whole screen for a role. Anyone below it sees why instead. */
export function RequireRole({
  role, me, children, fallback,
}: { role: Role; me: Me | null | undefined; children: React.ReactNode; fallback?: React.ReactNode }) {
  const reason = roleBlock(me, role);
  if (!reason) return <>{children}</>;
  return (
    <>
      {fallback ?? (
        <div className="state-box" role="status">
          <Lock aria-hidden="true" />
          <div className="state-title">{reason}</div>
        </div>
      )}
    </>
  );
}
